import React, { useState, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Alert,
  Link,
  Divider,
  IconButton
} from '@mui/material';
import {
  Security,
  VpnKey,
  Close as CloseIcon
} from '@mui/icons-material';
import axios from '../utils/axios';
import { useAuth } from '../contexts/AuthContext';

const MFALogin = ({ open, onClose, onSuccess, userCredentials }) => {
  const { handleSuccessfulAuth } = useAuth();
  const [mfaCode, setMfaCode] = useState('');
  const [backupCode, setBackupCode] = useState('');
  const [useBackupCode, setUseBackupCode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const mfaInputRef = useRef(null);
  const backupCodeInputRef = useRef(null);

  const handleMFALogin = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await axios.post('/auth/login/mfa', {
        email: userCredentials.email,
        password: userCredentials.password,
        mfa_code: mfaCode
      });
      
      // Update auth context properly using the helper function
      handleSuccessfulAuth(response.data);
      
      // Call success callback
      onSuccess(response.data);
      onClose();
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Invalid MFA code';
      setError(errorMsg);
      
      // Clear the code so user can easily retry
      setMfaCode('');
      // Focus the input after a short delay for better UX
      setTimeout(() => {
        mfaInputRef.current?.focus();
      }, 100);
      // DO NOT call onSuccess or onClose on error - keep modal open
    } finally {
      setLoading(false);
    }
  };

  const handleBackupCodeLogin = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await axios.post('/auth/login/backup-code', {
        email: userCredentials.email,
        password: userCredentials.password,
        backup_code: backupCode
      });
      
      // Update auth context properly using the helper function
      handleSuccessfulAuth(response.data);
      
      // Call success callback
      onSuccess(response.data);
      onClose();
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Invalid backup code';
      setError(errorMsg);
      
      // Clear the code so user can easily retry
      setBackupCode('');
      // Focus the input after a short delay for better UX
      setTimeout(() => {
        backupCodeInputRef.current?.focus();
      }, 100);
      // DO NOT call onSuccess or onClose on error - keep modal open
    } finally {
      setLoading(false);
    }
  };

  const toggleBackupCode = () => {
    setUseBackupCode(!useBackupCode);
    setError('');
    setMfaCode('');
    setBackupCode('');
  };

  return (
    <Dialog 
      open={open} 
      onClose={(event, reason) => {
        if (reason === 'escapeKeyDown') {
          onClose();
        }
        // Prevent closing on backdrop click
      }} 
      maxWidth="sm" 
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <Security />
            Two-Factor Authentication Required
          </Box>
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Typography variant="body2" color="textSecondary" mb={3}>
          Please enter your authentication code to complete sign in
        </Typography>
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {!useBackupCode ? (
          <Box>
            <TextField
              fullWidth
              label="Authentication Code"
              value={mfaCode}
              onChange={(e) => {
                setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6));
                setError(''); // Clear error when user starts typing
              }}
              placeholder="000000"
              helperText="Enter the 6-digit code from your authenticator app"
              inputRef={mfaInputRef}
              inputProps={{ 
                maxLength: 6,
                style: { textAlign: 'center', fontSize: '1.5rem', letterSpacing: '0.5rem' }
              }}
              sx={{ mb: 3 }}
            />
            
            <Box textAlign="center" mb={2}>
              <Link 
                component="button"
                variant="body2"
                onClick={toggleBackupCode}
                underline="hover"
              >
                Can't access your authenticator? Use a backup code
              </Link>
            </Box>
          </Box>
        ) : (
          <Box>
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body2">
                Backup codes are for emergency access only. Each code can only be used once.
              </Typography>
            </Alert>
            
            <TextField
              fullWidth
              label="Backup Code"
              value={backupCode}
              onChange={(e) => {
                setBackupCode(e.target.value.replace(/[^0-9-]/g, '').slice(0, 9));
                setError(''); // Clear error when user starts typing
              }}
              placeholder="0000-0000"
              helperText="Enter one of your saved backup codes"
              inputRef={backupCodeInputRef}
              inputProps={{ 
                style: { textAlign: 'center', fontSize: '1.2rem', letterSpacing: '0.2rem' }
              }}
              sx={{ mb: 3 }}
            />
            
            <Box textAlign="center" mb={2}>
              <Link 
                component="button"
                variant="body2"
                onClick={toggleBackupCode}
                underline="hover"
              >
                ← Back to authenticator code
              </Link>
            </Box>
          </Box>
        )}
        
        <Divider sx={{ my: 2 }} />
        
        <Alert severity="info">
          <Typography variant="body2">
            <strong>Lost access?</strong> Contact your administrator for help with MFA recovery.
          </Typography>
        </Alert>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose}>
          Cancel
        </Button>
        <Button 
          variant="contained"
          onClick={useBackupCode ? handleBackupCodeLogin : handleMFALogin}
          disabled={
            loading || 
            (!useBackupCode && mfaCode.length !== 6) ||
            (useBackupCode && backupCode.length < 8)
          }
          startIcon={useBackupCode ? <VpnKey /> : <Security />}
        >
          {loading ? 'Verifying...' : 'Sign In'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MFALogin;